import t from '@jetshop/intl';
import Accordion from '@jetshop/ui/Accordion/Accordion';
import React, { useState } from 'react';
import { styled } from 'linaria/react';
import {
  ProductSection as UIProductSection,
  SectionHeading
} from './ProductInfo';
import { theme } from '../Theme';
import Sizeguide from './Sizeguide';

import { ReactComponent as AngleDown } from '../../svg/fa/angle-down.svg';
import { ReactComponent as AngleRight } from '../../svg/fa/angle-right.svg';
import { useQuery } from 'react-apollo';

import CategoryQuery from './CategoryQuery.gql';
import CategoryContentQuery from '../StartPage/ContentRendererComponents/DynamicCategoryRenderer/CategoryContentQuery.gql';
import {
  includeFieldsWithKey,
  ProductCustomFieldsSpec,
  productHasCustomFields
} from './ProductCustomFieldsSpec';

const ProductSection = styled(UIProductSection)`
  border-top: 1px solid #d2d2d2;
  font-size: 0.875rem;
  width: 100% !important;
  padding-bottom: 0;
  margin-left: 0;
  &:last-of-type {
    border-bottom: 1px solid #d2d2d2;
  }
  &:first-of-type {
    margin-top: 2.5rem;
  }
  & > div {
    a {
      color: ${theme.colors.accent};
      transition: all ease 0.3s;
    }
    a:hover {
      color: black;
      transition: all ease 0.3s;
    }

    img {
      height: auto;
      max-width: 100%;
      padding: 0.5rem 0;
    }
  }
  ${SectionHeading} {
    align-items: center;
    cursor: pointer;
    display: flex;
    font-weight: bold;
    justify-items: space-between;
    margin-bottom: 0;
    padding: 8px 0;
    font-family: ${theme.fonts.body};
    font-size: 14px;
    + * > :first-child {
      margin-top: 0rem;
      margin-bottom: 1rem;
    }
    ${theme.below.md} {
      font-size: 16px;
    }
  }

  .accordion-content {
    ${theme.below.md} {
      font-size: 12px;
    }
    &.material {
      & > div {
        display: flex;
        p {
          padding-left: 10px;
        }
      }
    }
    &.address {
      & > div {
        display: flex;
        flex-direction: column;
        p {
          margin-bottom: 0;
        }
      }
    }
  }

  .size-guide-wrapper {
    display: none;
  }
  article h2:first-child {
    display: none;
  }
`;

const IndicatorIcon = styled('span')`
  margin-left: auto;
  margin-right: 1rem;
  width: auto;
  height: auto;
  display: none;
`;

const ImportantInfo = styled('div')`
  background: ${theme.colors.tablegrey};
  margin: 1rem 0;
  padding: 0.5rem 1rem;

  p:last-of-type {
    margin-bottom: 8px !important;
  }

  h3 {
    display: inline-block;
    padding-top: 5px;
  }

  svg {
    font-size: 24px;
    * {
      fill: ${theme.colors.orange};
    }
  }
  &.warning {
    svg {
      * {
        fill: ${theme.colors.red};
      }
    }
  }

  div p img {
    padding: 0;
  }
`;

const ProductProperties = styled('ul')`
  list-style: disc inside;
  margin-top: 0.75rem;
  margin-bottom: 10px;
`;

const YoutubeVideo = styled('div')`
  margin: 1.5rem 0;
  iframe {
    width: 100%;
  }
`;
const Content = styled('div')`
  ul {
    list-style: disc inside;
  }
  ol {
    list-style: decimal inside;
  }
`;

const PaymentInfo = () => {
  const id = 1070;
  const { data } = useQuery(CategoryQuery, { variables: { id: id } });
  if (!data) return null;
  return <div dangerouslySetInnerHTML={{ __html: data.category.content }} />;
};

// Function to filter items by brand name
const filterItemsByBrandName = (data, brandName) => {
  return data?.category?.data?.items?.filter(item => {
    return item?.properties?.some(
      property =>
        property?.name === 'brandName' && property?.value?.value === brandName
    );
  });
};

// Function to filter items by brand name
const filterItemsByArticleNo = (matchingItems, articleNo) => {
  return matchingItems?.filter(item => {
    return item?.children?.some(
      child =>
        child?.properties[0]?.name === 'articleNumber' &&
        child?.properties[0]?.value?.value === articleNo
    );
  });
};

const getMainAddress = matchingItems => {
  return matchingItems?.filter(item => {
    return item?.children?.length === 0;
  });
};

const ManufacturerInfo = ({ manufacturer }) => {
  const manufacturerAdressesCategoryId = 3695;
  const targetBrand = manufacturer?.name;
  const targetArticleNo = manufacturer?.productArtNo;
  const { data, error } = useQuery(CategoryContentQuery, {
    variables: {
      id: manufacturerAdressesCategoryId
    }
  });
  if (!data || error) {
    return null;
  }

  // Get all items with the specified brand name
  const matchingItems = filterItemsByBrandName(data, targetBrand);
  if (matchingItems?.length === 1) {
    return (
      <div
        dangerouslySetInnerHTML={{
          __html: matchingItems[0]?.properties?.find(
            property => property?.name === 'text'
          )?.value?.value
        }}
      />
    );
  } else if (matchingItems?.length > 1) {
    const matchingProducts = filterItemsByArticleNo(
      matchingItems,
      targetArticleNo
    );
    const mainAddress = getMainAddress(matchingItems);

    if (matchingProducts?.length === 0) {
      return (
        <div
          dangerouslySetInnerHTML={{
            __html: mainAddress[0]?.properties?.find(
              property => property?.name === 'text'
            )?.value?.value
          }}
        />
      );
    } else {
      return (
        <div
          dangerouslySetInnerHTML={{
            __html: matchingProducts[0]?.properties?.find(
              property => property?.name === 'text'
            )?.value?.value
          }}
        />
      );
    }
  } else {
    return <h3>{t('Information will be available soon')}</h3>;
  }
};

const AccordionIndicator = ({ isOpen }) => (
  <IndicatorIcon>{isOpen ? <AngleDown /> : <AngleRight />}</IndicatorIcon>
);

const getProductDescription = product => {
  // second check for 'LongDescription'
  const longDescription = product.customFields.find(
    field => field.key === 'LongDescription'
  );

  // For now return the product description or an empty string
  return product?.description || '';
};

const ProductInfoAccordion = ({
  product,
  material,
  materialInfo,
  goodtoknow,
  artNo_partial,
  manufacturer,
  warningInfo,
  importantInfoHeadline,
  importantInfo
}) => {
  // Prefer Measurements (string). Fallback to ProductMeasureLists (legacy array).
  const measurementField =
    product.customFields.find(({ key }) => key === 'Measurements') ??
    product.customFields.find(({ key }) => key === 'ProductMeasureLists');
  const measurementLists = [].concat(
    measurementField?.stringValue ?? measurementField?.listValues ?? []
  );


  const productDescription = getProductDescription(product);

  return (
    <>
      <Accordion single initialOpenIndexes={[0]}>
        {({ openIndexes, handleClick, AccordionContent }) => (
          <Content>
            {!!productDescription && (
              <ProductSection>
                <SectionHeading onClick={() => handleClick(0)}>
                  <span>{t('Product info')}</span>
                  <AccordionIndicator isOpen={openIndexes.includes(0)} />
                </SectionHeading>
                <AccordionContent isOpen={openIndexes.includes(0)}>
                  <div
                    className="accordion-content"
                    dangerouslySetInnerHTML={{ __html: productDescription }}
                  />
                </AccordionContent>
                {importantInfoHeadline || importantInfo ? (
                    <ImportantInfo>
                      {importantInfoHeadline ? (
                        <h3>{importantInfoHeadline.value}</h3>
                      ) : null}
                      {importantInfo ? (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: importantInfo.value
                          }}
                        />
                      ) : null}
                    </ImportantInfo>
                  ) : null}
                  {warningInfo ? (
                    <ImportantInfo className="warning">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: warningInfo.value
                        }}
                      />
                    </ImportantInfo>
                  ) : null}
              </ProductSection>
            )}

            {productHasCustomFields(product) ? (
              <ProductSection>
                <SectionHeading onClick={() => handleClick(1)}>
                  <span>{t('Product specification')}</span>
                  <AccordionIndicator isOpen={openIndexes.includes(1)} />
                </SectionHeading>
                <AccordionContent isOpen={openIndexes.includes(1)}>
                  <div className="accordion-content">
                    <ProductCustomFieldsSpec product={product} />
                  </div>
                </AccordionContent>
              </ProductSection>
            ) : null}

            {true ? (
              <ProductSection>
                <SectionHeading onClick={() => handleClick(2)}>
                  <span>{t('Manufacturer')}</span>
                  <AccordionIndicator isOpen={openIndexes.includes(2)} />
                </SectionHeading>
                <AccordionContent isOpen={openIndexes.includes(2)}>
                  <div className="accordion-content address">
                    {manufacturer?.id !== null && (
                      <ManufacturerInfo manufacturer={manufacturer} />
                    )}
                  </div>
                </AccordionContent>
              </ProductSection>
            ) : null}

            {measurementLists.length ? (
              <ProductSection>
                <SectionHeading>
                  <Sizeguide product={product} measurementLists={measurementLists}>
                    {t('Size guide')}
                    <AccordionIndicator isOpen={openIndexes.includes(3)} />
                  </Sizeguide>
                </SectionHeading>
                <AccordionContent
                  isOpen={openIndexes.includes(3)}
                ></AccordionContent>
              </ProductSection>
            ) : null}

            {goodtoknow ? (
              <ProductSection>
                <SectionHeading onClick={() => handleClick(4)}>
                  <span>{t('Good to know')}</span>
                  <AccordionIndicator isOpen={openIndexes.includes(4)} />
                </SectionHeading>
                <AccordionContent isOpen={openIndexes.includes(4)}>
                  <div className="accordion-content">
                    {goodtoknow ? (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: goodtoknow.value
                        }}
                      />
                    ) : null}
                  </div>
                </AccordionContent>
              </ProductSection>
            ) : null}

            <ProductSection>
              <SectionHeading onClick={() => handleClick(5)}>
                <span>{t('Delivery & Return')}</span>
                <AccordionIndicator isOpen={openIndexes.includes(5)} />
              </SectionHeading>
              <AccordionContent isOpen={openIndexes.includes(5)}>
                <div className="accordion-content">
                  <PaymentInfo />
                </div>
              </AccordionContent>
            </ProductSection>
            {/* <ProductSection>
              <SectionHeading onClick={() => handleClick(6)}>
                <span>{t('Reviews & FAQ')}</span>
                <AccordionIndicator isOpen={openIndexes.includes(6)} />
              </SectionHeading>
              <AccordionContent isOpen={openIndexes.includes(6)}>
                <div className="accordion-content" id="allReviews">
                  <div
                    style={{ marginTop: '4px' }}
                    id="gmf-comment-section"
                    data-product-id={artNo_partial}
                  ></div>
                </div>
              </AccordionContent>
            </ProductSection> */}
          </Content>
        )}
      </Accordion>
    </>
  );
};

export default ProductInfoAccordion;
